import {
  accountIdFormat,
  inventoryItemIdFormat,
  locationIdFormat,
  packingTemplateIdFormat,
  processingSessionIdFormat,
  productIdFormat,
} from "@redotech/redo-model/ids";
import { packingMode } from "@redotech/redo-model/inventory/packing-mode";
import { processingEventTypes } from "@redotech/redo-return-warehouse-processing-db/processing-types";
import * as z from "zod/v4";
import {
  InventoryItemDispositionSchema,
  InventoryItemGradeSchema,
  InventoryItemKindSchema,
} from "./create-inventory-item";
import { ReturnProcessingFlow } from "./get-return-for-processing";
import {
  ItemProcessingStatusSchema,
  ReturnsProcessingSessionTypeSchema,
  SessionStatus,
} from "./processing-session";

const ItemProcessingProduct_ = z.object({
  id: productIdFormat.schema,
  title: z.string(),
  imageUrl: z.string().nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  currentInventory: z.number().nullable(),
  packingTemplateId: packingTemplateIdFormat.schema.nullable(),
  unitsPerBox: z.number().nullable(),
  packingMode: z.enum(packingMode),
  trackLotFulfillment: z.boolean(),
});
export const ItemProcessingProduct: z.ZodType<ItemProcessingProduct> =
  ItemProcessingProduct_;
export interface ItemProcessingProduct
  extends z.infer<typeof ItemProcessingProduct_> {}

const ItemProcessingInventoryItem_ = z.object({
  id: inventoryItemIdFormat.schema,
  kind: InventoryItemKindSchema,
  locationId: locationIdFormat.schema,
  returnId: z.string().nullable(),
  productId: productIdFormat.schema.nullable(),
  quantity: z.number(),
  grade: InventoryItemGradeSchema.nullable(),
  disposition: InventoryItemDispositionSchema.nullable(),
  createdAt: z.date(),
  product: z
    .object({
      id: productIdFormat.schema,
      title: z.string(),
      sku: z.string().nullable(),
      imageUrl: z.string().nullable(),
    })
    .nullable(),
});
export const ItemProcessingInventoryItem: z.ZodType<ItemProcessingInventoryItem> =
  ItemProcessingInventoryItem_;
export interface ItemProcessingInventoryItem
  extends z.infer<typeof ItemProcessingInventoryItem_> {}

const ItemProcessingSession_ = z.object({
  id: processingSessionIdFormat.schema,
  accountId: accountIdFormat.schema,
  legacyUserId: z.string(),
  type: ReturnsProcessingSessionTypeSchema.nullable(),
  locationId: locationIdFormat.schema.nullable(),
  productId: productIdFormat.schema.nullable(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  status: ItemProcessingStatusSchema,
  grade: InventoryItemGradeSchema.nullable(),
  outcome: InventoryItemDispositionSchema.nullable(),
  notes: z.string().nullable(),
  lifecycleStatus: z.enum(SessionStatus),
  createdAt: z.date(),
  updatedAt: z.date(),
  completedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
});
export const ItemProcessingSession: z.ZodType<ItemProcessingSession> =
  ItemProcessingSession_;
export interface ItemProcessingSession
  extends z.infer<typeof ItemProcessingSession_> {}

const ItemProcessingSessionSubject_ = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("inventory_item"),
    inventoryItem: ItemProcessingInventoryItem,
  }),
  z.object({ type: z.literal("product"), product: ItemProcessingProduct }),
]);
export type ItemProcessingSessionSubject = z.infer<
  typeof ItemProcessingSessionSubject_
>;

const ItemProcessingSessionDetails_ = z.object({
  session: ItemProcessingSession,
  subject: ItemProcessingSessionSubject_,
  returnProcessingFlow: ReturnProcessingFlow,
});
export type ItemProcessingSessionDetails = z.infer<
  typeof ItemProcessingSessionDetails_
>;

const InventoryItemLookupResult_ = z.object({
  type: z.literal("inventory_item"),
  inventoryItem: ItemProcessingInventoryItem,
  activeSession: ItemProcessingSession.nullable(),
});

const ProductLookupResult_ = z.object({
  type: z.literal("product"),
  product: ItemProcessingProduct,
});

const MultipleProductsLookupResult_ = z.object({
  type: z.literal("multiple_products"),
  products: z.array(ItemProcessingProduct),
});

const NotFoundLookupResult_ = z.object({ type: z.literal("not_found") });

const LookupItemForProcessingResult_ = z.discriminatedUnion("type", [
  InventoryItemLookupResult_,
  ProductLookupResult_,
  MultipleProductsLookupResult_,
  NotFoundLookupResult_,
]);
export type LookupItemForProcessingResult = z.infer<
  typeof LookupItemForProcessingResult_
>;

export const lookupItemForProcessing = {
  name: "lookupItemForProcessing" as const,
  input: z.object({
    identifier: z.string().min(1).max(255),
    locationId: locationIdFormat.schema,
  }),
  output: z.object({ result: LookupItemForProcessingResult_ }),
};

const ItemProcessingSource_ = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("inventory_item"),
    inventoryItemId: inventoryItemIdFormat.schema,
  }),
  z.object({ type: z.literal("product"), productId: productIdFormat.schema }),
]);

export const createItemProcessingSession = {
  name: "createItemProcessingSession" as const,
  input: z.object({
    source: ItemProcessingSource_,
    locationId: locationIdFormat.schema,
  }),
  output: z.object({ session: ItemProcessingSession }),
};

export const getItemProcessingSession = {
  name: "getItemProcessingSession" as const,
  input: z
    .object({
      sessionId: processingSessionIdFormat.schema.optional(),
      inventoryItemId: inventoryItemIdFormat.schema.optional(),
    })
    .refine(
      (input) =>
        (input.sessionId === undefined) !==
        (input.inventoryItemId === undefined),
      { message: "Provide exactly one of sessionId or inventoryItemId" },
    ),
  output: z.object({ details: ItemProcessingSessionDetails_.nullable() }),
};

export const updateItemProcessingSession = {
  name: "updateItemProcessingSession" as const,
  input: z.object({
    sessionId: processingSessionIdFormat.schema,
    status: ItemProcessingStatusSchema.optional(),
    grade: InventoryItemGradeSchema.nullish(),
    outcome: InventoryItemDispositionSchema.nullish(),
    notes: z.string().nullish(),
  }),
  output: z.object({ session: ItemProcessingSession }),
};

export const completeItemProcessingSession = {
  name: "completeItemProcessingSession" as const,
  input: z.object({ sessionId: processingSessionIdFormat.schema }),
  output: z.object({ session: ItemProcessingSession }),
};

export const logItemProcessingEvent = {
  name: "logItemProcessingEvent" as const,
  input: z.object({
    type: z.enum(processingEventTypes),
    sessionId: processingSessionIdFormat.schema,
    flowId: z.string().optional(),
    flowStepId: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
  }),
  output: z.object({ success: z.boolean() }),
};
